import { Helmet } from 'react-helmet-async';
import { H1, H2, H3, Body } from '../components/ui/Typography';

export default function Terms() {
  return (
    <>
      <Helmet>
        <title>RentFi | Terms of Use</title>
        <meta 
          name="description" 
          content="Read RentFi's Terms of Service to understand the rules, guidelines, and agreements that govern the use of our real estate investment platform and services." 
        />
        <meta 
          name="keywords" 
          content="RentFi terms of service, terms and conditions, user agreement, legal terms, real estate investment terms" 
        />
        <link rel="canonical" href="https://rentfi.io/terms" />
      </Helmet>
      <div className="min-h-screen bg-gray-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <H1 className="text-primary mb-4">Terms of Use</H1>
            <p className="text-gray-500 mb-8">Last Updated: January 30, 2025</p>

            {/* Introduction */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">1. Introduction</H2>
              <Body className="text-gray-600">
                Welcome to RentFi ("Company", "we", "our", "us"). These Terms of Use govern your access to and use of the RentFi platform, including our website (www.rentfi.io), services, applications, and all content offered through our platform (collectively, the "Services").
              </Body>
            </div>

            {/* Acceptance of Terms */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">2. Acceptance of Terms</H2>
              <Body className="text-gray-600">
                By accessing or using our Services, you agree to be bound by these Terms of Use. If you do not agree to these terms, do not use our Services.
              </Body>
            </div>

            {/* Services Description */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">3. Services Description</H2>
              <Body className="text-gray-600 mb-4">RentFi provides a blockchain-based platform that enables users to:</Body>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Purchase and hold RENT tokens</li>
                <li>Participate in real estate investment opportunities</li>
                <li>Receive periodic distributions based on rental income</li>
                <li>Access real estate investment analytics and reporting</li>
              </ul>
            </div>

            {/* User Eligibility */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">4. User Eligibility</H2>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Users must be at least 18 years old</li>
                <li>Users must have the legal capacity to enter into binding contracts</li>
                <li>Users must comply with all applicable laws and regulations</li>
                <li>Users must not be residents of restricted jurisdictions</li>
              </ul>
            </div>

            {/* Token Related Terms */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">5. Token Related Terms</H2>
              
              <div className="space-y-6">
                <div>
                  <H3 className="text-gray-900 mb-2">5.1 RENT Tokens</H3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>RENT tokens do not represent ownership rights in real estate</li>
                    <li>RENT tokens do not constitute securities or financial instruments</li>
                    <li>Minimum holding requirement of 1,000 RENT tokens for distribution eligibility</li>
                    <li>Token distributions are subject to snapshot verification</li>
                  </ul>
                </div>

                <div>
                  <H3 className="text-gray-900 mb-2">5.2 Token Distribution</H3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>Distribution occurs monthly on the 5th of each month</li>
                    <li>Based on verified wallet holdings during the snapshot period</li>
                    <li>Subject to smart contract execution and verification</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Prohibited Activities */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">6. Prohibited Activities</H2>
              <Body className="text-gray-600 mb-4">Users shall not:</Body>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Attempt to circumvent platform security measures</li>
                <li>Manipulate or game the distribution system</li>
                <li>Provide false or misleading information</li>
                <li>Engage in market manipulation</li>
                <li>Use the platform for illegal activities</li>
              </ul>
            </div>

            {/* Intellectual Property Rights */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">7. Intellectual Property Rights</H2>
              <Body className="text-gray-600">
                All content, trademarks, software, and technology used on our platform are owned by RentFi and protected by applicable intellectual property laws.
              </Body>
            </div>

            {/* Limitation of Liability */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">8. Limitation of Liability</H2>
              <Body className="text-gray-600 mb-4">To the maximum extent permitted by law, RentFi shall not be liable for:</Body>
              <ul className="list-disc pl-6 space-y-2 text-gray-600">
                <li>Investment losses</li>
                <li>Technical platform issues</li>
                <li>Market volatility</li>
                <li>Third-party actions</li>
                <li>Force majeure events</li>
              </ul>
            </div>

            {/* Modifications */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">9. Modifications</H2>
              <Body className="text-gray-600">
                We reserve the right to modify these Terms of Use at any time. Changes will be effective immediately upon posting to our platform.
              </Body>
            </div>

            {/* Termination */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">10. Termination</H2>
              <Body className="text-gray-600">
                We may terminate or suspend access to our Services immediately, without prior notice, for any reason.
              </Body>
            </div>

            {/* Governing Law */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">11. Governing Law</H2>
              <Body className="text-gray-600">
                These Terms of Use shall be governed by and construed in accordance with applicable laws.
              </Body>
            </div>

            {/* Contact Information */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">12. Contact Information</H2>
              <Body className="text-gray-600">
                For any questions regarding these Terms of Use:<br />
                <a 
                  href="mailto:support@rentfi.helpcrunch-mail.com" 
                  className="text-secondary hover:text-secondary-dark"
                  aria-label="Email RentFi support team with questions about Terms of Use"
                >
                  support@rentfi.helpcrunch-mail.com
                </a>
              </Body>
            </div>

            {/* Severability */}
            <div>
              <H2 className="text-primary mb-4">13. Severability</H2>
              <Body className="text-gray-600">
                If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full force and effect.
              </Body>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}